import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, MapPin, RefreshCw } from 'lucide-react';
import { fetchSinSede, fetchSedesCatalogo, asignarSede, fetchNmHuerfanos, asignarSedeNM, fetchCentrosCatalog, fetchNmResponsableOtro, asignarResponsableNM, fetchPersonasCatalog } from '@/services/api';
import { CentroCombobox } from '@/components/common/CentroCombobox';
import { PersonaCombobox } from '@/components/common/PersonaCombobox';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export function SinSedeTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState({});
  const [sedeAsign, setSedeAsign] = useState({});
  const [modulo, setModulo] = useState('OPS');
  const [nmSedeAsign, setNmSedeAsign] = useState({});
  const [assigningNm, setAssigningNm] = useState(null);
  const [respNombreInput, setRespNombreInput] = useState({});
  const [assigningResp, setAssigningResp] = useState(null);

  const { data: records = [], isLoading: loadingRec, refetch } = useQuery({
    queryKey: ['sin-sede'],
    queryFn: fetchSinSede,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
  const { data: nmHuerfanos, isLoading: loadingNm } = useQuery({
    queryKey: ['nm-huerfanos'],
    queryFn: fetchNmHuerfanos,
    // P0 03/09: el badge y la lista salen de la misma respuesta (nunca
    // estaban desincronizados entre sí), pero podían quedarse desactualizados
    // por minutos/horas si la pestaña se dejaba abierta mientras corría un
    // sync en background — sin refetch automático, sólo se veía el número
    // real tras un F5. Ahora: sin cache (staleTime 0), refetch al volver a
    // la pestaña/ventana, siempre fresco al montar, y poll cada 60s.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: 60000,
    enabled: modulo === 'NM',
  });
  const { data: catalogo = [] } = useQuery({
    queryKey: ['sedes-catalogo'],
    queryFn: fetchSedesCatalogo,
    staleTime: 300000,
  });
  // NM usa el catálogo canónico centros_catalog (codigo+nombre siempre
  // correctos) en vez de sedes-catalogo (derivado de ops_cache.sede_label,
  // que para códigos poco usados puede no ser un nombre legible — bug 31/08).
  const { data: centrosCatalog = [] } = useQuery({
    queryKey: ['centros-catalog-for-nm'],
    queryFn: () => fetchCentrosCatalog(),
    staleTime: 300000,
    enabled: modulo === 'NM',
  });
  const { data: respOtro, isLoading: loadingRespOtro } = useQuery({
    queryKey: ['nm-responsable-otro'],
    queryFn: fetchNmResponsableOtro,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: 60000,
    enabled: modulo === 'RespOtro',
  });
  const { data: personasCatalog = [] } = useQuery({
    queryKey: ['personas-catalog'],
    queryFn: fetchPersonasCatalog,
    staleTime: 300000,
    enabled: modulo === 'RespOtro',
  });

  const filtered = records.filter(r => r.modulo === modulo);
  const nmItems = nmHuerfanos?.items || [];
  const respOtroItems = respOtro?.items || [];
  const allSelected = filtered.length > 0 && filtered.every(r => selected[r.id]);

  const toggleAll = () => {
    const newSel = { ...selected };
    if (allSelected) { filtered.forEach(r => delete newSel[r.id]); }
    else { filtered.forEach(r => { newSel[r.id] = true; }); }
    setSelected(newSel);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const ids = Object.keys(selected).filter(id => selected[id] && sedeAsign[id]);
      if (!ids.length) throw new Error('Selecciona registros y asigna una sede');
      const grouped = {};
      for (const id of ids) {
        const rec = records.find(r => r.id === id);
        const key = rec.modulo;
        if (!grouped[key]) grouped[key] = { ids: [], modulo: key, ...sedeAsign[id] };
        grouped[key].ids.push(id);
      }
      await Promise.all(Object.values(grouped).map(g => asignarSede(g)));
    },
    onSuccess: () => {
      toast.success('Sedes asignadas correctamente');
      setSelected({});
      setSedeAsign({});
      qc.invalidateQueries(['sin-sede']);
      refetch();
    },
    onError: (e) => toast.error(e.message || 'Error al asignar'),
  });

  const handleAssignNm = async (objectid) => {
    const found = nmSedeAsign[objectid];
    if (!found) { toast.error('Selecciona una sede'); return; }
    setAssigningNm(objectid);
    try {
      await asignarSedeNM(objectid, { sede_codigo: found.codigo, sede_label: found.nombre });
      toast.success(`NM ${objectid} asignado a ${found.nombre}`);
      setNmSedeAsign(prev => { const n = { ...prev }; delete n[objectid]; return n; });
      qc.invalidateQueries({ queryKey: ['nm-huerfanos'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al asignar sede al Near Miss');
    } finally { setAssigningNm(null); }
  };

  const handleAssignResponsable = async (objectid) => {
    const nombre = (respNombreInput[objectid] || '').trim();
    if (!nombre) { toast.error('Escribe el nombre real del responsable'); return; }
    setAssigningResp(objectid);
    try {
      await asignarResponsableNM(objectid, { responsable_nombre: nombre });
      toast.success(`NM ${objectid}: responsable asignado a "${nombre}"`);
      setRespNombreInput(prev => { const n = { ...prev }; delete n[objectid]; return n; });
      qc.invalidateQueries({ queryKey: ['nm-responsable-otro'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al asignar responsable');
    } finally { setAssigningResp(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-montserrat font-semibold text-sm" style={{ color: 'var(--vz-text-primary)' }}>
            Registros sin sede asignada
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            {modulo === 'NM'
              ? 'Near Misses de ArcGIS sin centro_codigo asignado. La asignación se guarda como override y sobrevive a la próxima sincronización.'
              : modulo === 'RespOtro'
              ? 'Near Misses con responsable "Otro" — Survey123 no capturó el nombre real (sin campo libre para este caso). Escríbelo a mano; se guarda como override y sobrevive a la sincronización.'
              : (<>Registros con <code className="bg-black/10 px-1 rounded">otrocentro</code> informado pero sin sede en el catálogo.</>)}
          </p>
        </div>
        <button onClick={() => refetch()} className="p-1.5 rounded-lg transition-colors" style={{ background: 'var(--vz-bg-secondary)' }}>
          <RefreshCw size={14} style={{ color: 'var(--vz-text-muted)' }} />
        </button>
      </div>

      <div className="flex gap-2">
        {['OPS', 'Liderazgo', 'NM', 'RespOtro'].map(m => (
          <button key={m} onClick={() => setModulo(m)}
            className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
            style={{ background: modulo === m ? 'var(--vz-blue)' : 'var(--vz-bg-secondary)', color: modulo === m ? '#fff' : 'var(--vz-text-muted)' }}
            data-testid={`sin-sede-tab-${m.toLowerCase()}`}
          >
            {m === 'RespOtro' ? 'Resp. Otro' : m} ({
              m === 'NM' ? (nmHuerfanos?.total ?? '…')
              : m === 'RespOtro' ? (respOtro?.total ?? '…')
              : records.filter(r => r.modulo === m).length
            })
          </button>
        ))}
      </div>

      {modulo === 'RespOtro' ? (
        loadingRespOtro ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
        ) : respOtroItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>No hay Near Misses con responsable "Otro" sin resolver</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid="nm-responsable-otro-table">
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--vz-bg-secondary)', borderColor: 'var(--vz-border-subtle)' }}>
                  <TableHead className="text-xs font-semibold">Fecha</TableHead>
                  <TableHead className="text-xs font-semibold">Sede</TableHead>
                  <TableHead className="text-xs font-semibold">Rol</TableHead>
                  <TableHead className="text-xs font-semibold">Reportado por</TableHead>
                  <TableHead className="text-xs font-semibold">Descripción</TableHead>
                  <TableHead className="text-xs font-semibold min-w-[260px]">Nombre real del responsable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {respOtroItems.map((n, i) => (
                  <TableRow key={n.objectid || i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.fecha_reporte ? String(n.fecha_reporte).slice(0, 10) : '—'}</TableCell>
                    <TableCell className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{n.centro_nombre || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.responsable_rol || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.reportado_persona || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate" style={{ color: 'var(--vz-text-muted)' }} title={n.descripcion}>{n.descripcion || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <PersonaCombobox
                          personas={personasCatalog}
                          value={respNombreInput[n.objectid] || ''}
                          onSelect={nombre => setRespNombreInput(prev => ({ ...prev, [n.objectid]: nombre }))}
                          testId={`nm-resp-otro-select-${n.objectid}`}
                        />
                        <Button
                          size="sm" className="h-7 px-2 text-xs gap-1 flex-shrink-0"
                          disabled={assigningResp === n.objectid || !(respNombreInput[n.objectid] || '').trim()}
                          onClick={() => handleAssignResponsable(n.objectid)}
                          data-testid={`nm-resp-otro-assign-btn-${n.objectid}`}
                        >
                          {assigningResp === n.objectid ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : modulo === 'NM' ? (
        loadingNm ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
        ) : nmItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>No hay Near Misses sin sede</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid="nm-huerfanos-table">
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--vz-bg-secondary)', borderColor: 'var(--vz-border-subtle)' }}>
                  <TableHead className="text-xs font-semibold">Fecha</TableHead>
                  <TableHead className="text-xs font-semibold">Reportado por</TableHead>
                  <TableHead className="text-xs font-semibold">Clasificación</TableHead>
                  <TableHead className="text-xs font-semibold">Negocio</TableHead>
                  <TableHead className="text-xs font-semibold">Gerente</TableHead>
                  <TableHead className="text-xs font-semibold">Otro Centro (libre)</TableHead>
                  <TableHead className="text-xs font-semibold">Descripción</TableHead>
                  <TableHead className="text-xs font-semibold min-w-[220px]">Asignar Sede</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nmItems.map((n, i) => (
                  <TableRow key={n.objectid || i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.fecha_reporte ? String(n.fecha_reporte).slice(0, 10) : '—'}</TableCell>
                    <TableCell className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{n.reportado_persona || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.clasificacion_nm || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.negocio || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{n.gerente || '—'}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(255,107,53,0.12)', color: '#FF6B35' }}>
                        <AlertCircle size={10} /> {n.otrocentro || n.sede || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs max-w-[280px] truncate" style={{ color: 'var(--vz-text-muted)' }} title={n.descripcion}>{n.descripcion || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <CentroCombobox
                          centros={centrosCatalog}
                          value={nmSedeAsign[n.objectid]?.codigo || ''}
                          onSelect={found => setNmSedeAsign(prev => ({ ...prev, [n.objectid]: found }))}
                          testId={`nm-sede-select-${n.objectid}`}
                        />
                        <Button
                          size="sm" className="h-7 px-2 text-xs gap-1 flex-shrink-0"
                          disabled={assigningNm === n.objectid || !nmSedeAsign[n.objectid]}
                          onClick={() => handleAssignNm(n.objectid)}
                          data-testid={`nm-sede-assign-btn-${n.objectid}`}
                        >
                          {assigningNm === n.objectid ? <RefreshCw size={11} className="animate-spin" /> : <MapPin size={11} />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : loadingRec ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <CheckCircle size={32} style={{ color: 'var(--vz-green)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>No hay registros sin sede en {modulo}</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--vz-border-subtle)' }}>
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--vz-bg-secondary)', borderColor: 'var(--vz-border-subtle)' }}>
                  <TableHead className="w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
                  </TableHead>
                  <TableHead className="text-xs font-semibold">Persona</TableHead>
                  <TableHead className="text-xs font-semibold">Negocio</TableHead>
                  <TableHead className="text-xs font-semibold">Otro Centro (libre)</TableHead>
                  <TableHead className="text-xs font-semibold">Fecha</TableHead>
                  <TableHead className="text-xs font-semibold min-w-[220px]">Asignar Sede</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => (
                  <TableRow key={r.id} style={{ borderColor: 'var(--vz-border-subtle)', background: selected[r.id] ? 'rgba(74,158,255,0.06)' : 'transparent' }}>
                    <TableCell>
                      <input type="checkbox" checked={!!selected[r.id]}
                        onChange={e => setSelected(prev => ({ ...prev, [r.id]: e.target.checked }))}
                        className="rounded"
                      />
                    </TableCell>
                    <TableCell className="text-xs font-medium" style={{ color: 'var(--vz-text-primary)' }}>{r.persona || '—'}</TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{r.negocio || '—'}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(255,107,53,0.12)', color: '#FF6B35' }}>
                        <AlertCircle size={10} /> {r.otrocentro}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{r.fecha || '—'}</TableCell>
                    <TableCell>
                      <Select
                        value={sedeAsign[r.id]?.sede_label || ''}
                        onValueChange={val => {
                          const found = catalogo.find(c => c.sede_label === val);
                          if (found) setSedeAsign(prev => ({ ...prev, [r.id]: found }));
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs w-full" data-testid={`sede-select-${r.id}`}>
                          <SelectValue placeholder="Selecciona sede…" />
                        </SelectTrigger>
                        <SelectContent>
                          {catalogo.map(c => (
                            <SelectItem key={c.sede_label} value={c.sede_label} className="text-xs">
                              {c.sede_label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-2">
            <span className="text-xs self-center" style={{ color: 'var(--vz-text-muted)' }}>
              {Object.keys(selected).filter(id => selected[id]).length} seleccionados
            </span>
            <Button
              data-testid="btn-asignar-sede" size="sm"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || Object.keys(selected).filter(id => selected[id] && sedeAsign[id]).length === 0}
              className="text-xs gap-1.5"
            >
              {mutation.isPending ? <RefreshCw size={12} className="animate-spin" /> : <MapPin size={12} />}
              Asignar Sede
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
